import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { translations } from "./index";
import { DEFAULT_LOCALE, LOCALE_CODES } from "./config";

const I18nContext = createContext(null);

function resolvePath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    const idx = /^\d+$/.test(key) ? Number(key) : key;
    return acc[idx];
  }, obj);
}

export function I18nProvider({ children }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const locale = LOCALE_CODES.includes(params.lang) ? params.lang : DEFAULT_LOCALE;

  const t = useCallback(
    (path) => {
      const primary = resolvePath(translations[locale], path);
      if (primary !== undefined) return primary;
      const fallback = resolvePath(translations.en, path);
      return fallback !== undefined ? fallback : path;
    },
    [locale]
  );

  const changeLocale = useCallback(
    (next) => {
      const rest = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "");
      navigate(`/${next}${rest || ""}${location.search}`);
    },
    [location, navigate]
  );

  const value = useMemo(() => ({ locale, t, changeLocale }), [locale, t, changeLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
